import { useRef, useState, useCallback } from 'react';
import type { ProviderSearchResult } from '@/lib/api/hooks';
import { ProviderCarouselCard } from './ProviderCarouselCard';
import { IonIcon } from '@ionic/react';
import { chevronBack, chevronForward } from 'ionicons/icons';

interface Props {
  providers: ProviderSearchResult[];
}

export function ProviderCarousel({ providers }: Props) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const onScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const scrollLeft = el.scrollLeft;
    const cardWidth = el.querySelector<HTMLElement>('.bp-pc-card')?.offsetWidth ?? 340;
    const gap = 24;
    const idx = Math.round(scrollLeft / (cardWidth + gap));
    setActiveIdx(Math.max(0, Math.min(idx, providers.length - 1)));
  }, [providers.length]);

  const scrollBy = (direction: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    const cardWidth = el.querySelector<HTMLElement>('.bp-pc-card')?.offsetWidth ?? 340;
    const offset = direction === 'left' ? -(cardWidth + 24) : cardWidth + 24;
    el.scrollBy({ left: offset, behavior: 'smooth' });
  };

  const scrollTo = (idx: number) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelectorAll<HTMLElement>('.bp-pc-card')[idx];
    if (card) {
      card.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
    setActiveIdx(idx);
  };

  if (!providers.length) return null;

  return (
    <div className="bp-pc-wrapper">
      {/* Navigation arrows for desktop */}
      <button
        className="bp-pc-arrow bp-pc-arrow-left"
        onClick={() => scrollBy('left')}
        aria-label="Previous provider"
      >
        <IonIcon icon={chevronBack} />
      </button>
      <button
        className="bp-pc-arrow bp-pc-arrow-right"
        onClick={() => scrollBy('right')}
        aria-label="Next provider"
      >
        <IonIcon icon={chevronForward} />
      </button>

      <div
        ref={scrollRef}
        className="bp-pc-track"
        onScroll={onScroll}
      >
        {providers.map((p, i) => (
          <div key={p._id} className="bp-pc-slide">
            <ProviderCarouselCard provider={p} index={i} />
          </div>
        ))}
      </div>

      {providers.length > 1 && (
        <div className="bp-pc-dots">
          {providers.map((_, i) => (
            <button
              key={i}
              className={`bp-pc-dot ${i === activeIdx ? 'active' : ''}`}
              onClick={() => scrollTo(i)}
              aria-label={`Go to provider ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
